import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Modal,
  Pagination,
  Paper,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import ReactJson from "react-json-view";
import { CgClose } from "react-icons/cg";
import { FaFileUpload } from "react-icons/fa";
import {
  get_hts_by_chapter,
  upload_hts_json,
  get_chapter_numbers,
} from "../services/apis";
import { HiArrowNarrowLeft } from "react-icons/hi";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const chapters = Array.from(
  { length: 99 },
  (_, i) => `Chapter - ${String(i + 1).padStart(2, "0")}`
);
const ITEMS_PER_PAGE = 9;

export default function ChapterAccordion() {
  const [expanded, setExpanded] = useState(false);
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [jsonData, setJsonData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [jsonDataByChapter, setJsonDataByChapter] = useState({});
  const [isLoadingHts, setIsLoadingHts] = useState(true);
  const [currentOpenChapter, setCurrentOpenChapter] = useState([]);
  const [isDragging, setIsDragging] = useState(false);

  const navigate = useNavigate();

  const handleGetChapter = async () => {
    try {
      const response = await get_chapter_numbers();
      console.log("response", response.data.chapter_number);
    } catch (error) {
      console.log("Error:", error);
    }
  };

  const handleGetDataByHts = async (e) => {
    setJsonDataByChapter([]);
    const str = e;
    const chapterNumber = str.split("-")[1]?.trim();
    setCurrentOpenChapter(chapterNumber);

    setIsLoadingHts(true);
    try {
      const response = await get_hts_by_chapter(chapterNumber);
      setJsonDataByChapter(response.data.hts_codes);
    } catch (error) {
      console.log("Error:", error);
    } finally {
      setTimeout(() => {
        setIsLoadingHts(false);
      }, 500);
    }
  };

  const handleChange = (panel) => (_, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handlePageChange = (_, value) => {
    setPage(value);
    setExpanded(false);
  };

  const paginatedChapters = chapters.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  const handleOpenModal = (chapter) => {
    setSelectedChapter(chapter);
    setSelectedFile(null);
    setJsonData(null);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const isJson =
      file.type === "application/json" ||
      file.name.toLowerCase().endsWith(".json");

    if (!isJson || file.size > 10 * 1024 * 1024) {
      alert("Only JSON files under 10MB are allowed.");
      return;
    }

    setSelectedFile(file);
  };

  const handleSubmitFile = async () => {
    if (!selectedFile || !selectedChapter) return;

    const formData = new FormData();
    formData.append("file", selectedFile);

    const chapterNumber = selectedChapter.split("-")[1].trim();

    setIsSubmitting(true);
    try {
      const { data, error } = await upload_hts_json(formData, chapterNumber);
      if (error) throw error;
      toast.success("File Uploaded Successfully");
      handleGetDataByHts(selectedChapter);
      setModalOpen(false);
    } catch (err) {
      console.error("Upload error:", err);
      toast.error("Failed to upload JSON file.");
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    handleGetChapter();
  }, []);

  return (
    <Box sx={{ maxWidth: "90%", mx: "auto", my: 4 }}>
      <Typography
        variant="h5"
        color="#00A9CE"
        sx={{
          fontWeight: "bold",
          mb: 2,
          alignItems: "center",
          display: "flex",
        }}
      >
        <HiArrowNarrowLeft
          className="arrow-hover-animate"
          style={{
            marginRight: 8,
            cursor: "pointer",
            borderRadius: 20,
            padding: 3,
          }}
          onClick={() => navigate(-1)}
        />
        List of Chapters
      </Typography>

      {paginatedChapters.map((chapter) => (
        <Paper elevation={3} sx={{ mb: 2 }} key={chapter}>
          <Accordion
            expanded={expanded === chapter}
            onChange={handleChange(chapter)}
            sx={{ boxShadow: "none", "&:before": { display: "none" } }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              onClick={() =>
                chapter !== "Chapter - " + currentOpenChapter &&
                handleGetDataByHts(chapter)
              }
            >
              <Typography variant="h6">{chapter}</Typography>
              <Box sx={{ marginLeft: "auto" }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenModal(chapter);
                  }}
                  sx={{
                    background: "#00A9CE",
                    color: "white",
                    borderRadius: 2,
                    mr: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: "unset",
                    pt: 0.8,
                    fontSize: 12,
                  }}
                >
                  Upload JSON
                </Button>
              </Box>
            </AccordionSummary>

            <hr style={{ margin: "0 20px 5px 20px" }} />

            {!isLoadingHts ? (
              <>
                {jsonDataByChapter.length > 0 ? (
                  <AccordionDetails>
                    <ReactJson
                      src={jsonDataByChapter}
                      collapsed={true}
                      displayDataTypes={false}
                      enableClipboard={false}
                    />
                  </AccordionDetails>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      my: 2,
                    }}
                  >
                    <Typography sx={{ fontWeight: "bold" }}>
                      No Data Found
                    </Typography>
                  </Box>
                )}
              </>
            ) : (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100%"
                my={2}
              >
                <CircularProgress size={25} />
              </Box>
            )}
          </Accordion>
        </Paper>
      ))}

      <Box mt={3} display="flex" justifyContent="center">
        <Pagination
          count={Math.ceil(chapters.length / ITEMS_PER_PAGE)}
          page={page}
          onChange={handlePageChange}
          color="primary"
        />
      </Box>

      {/* Upload Modal */}
      <Modal open={modalOpen} onClose={handleCloseModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 500,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography variant="h6">
              Upload JSON to {selectedChapter}
            </Typography>
            <CgClose size={20} onClick={handleCloseModal} cursor={"pointer"} />
          </Box>

          <input
            id="json-input"
            type="file"
            hidden
            accept=".json"
            onChange={handleFileSelect}
          />

          {!selectedFile ? (
            <Box
              sx={{
                border: "2px dashed #ccc",
                borderRadius: 2,
                textAlign: "center",
                p: 4,
                cursor: "pointer",
                backgroundColor: isDragging ? "#f0faff" : "transparent",
                transition: "background-color 0.2s ease",
              }}
              onClick={() => document.getElementById("json-input").click()}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setIsDragging(false);
                const file = e.dataTransfer.files[0];
                if (!file) return;

                const isJson =
                  file.type === "application/json" ||
                  file.name.toLowerCase().endsWith(".json");

                if (!isJson || file.size > 10 * 1024 * 1024) {
                  alert("Only JSON files under 10MB are allowed.");
                  return;
                }

                setSelectedFile(file);
              }}
            >
              <FaFileUpload
                size={30}
                color="#00A9CE"
                style={{ marginBottom: 10 }}
              />
              <Typography>Select a file or drag and drop here</Typography>
              <Typography variant="body2" color="text.secondary" mt={1}>
                JSON files size no more than 10MB
              </Typography>

              <Button
                variant="outlined"
                sx={{ mt: 2, color: "#00A9CE" }}
                onClick={(e) => {
                  e.stopPropagation();
                  document.getElementById("json-input").click();
                }}
              >
                SELECT FILE
              </Button>
            </Box>
          ) : (
            <>
              <Typography mt={2} color="text.primary">
                Selected File : <strong>{selectedFile.name}</strong>
              </Typography>
              <Box mt={3}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={isSubmitting ? null : handleSubmitFile}
                  sx={{
                    background: "#00A9CE",
                    color: "white",
                    cursor: isSubmitting ? "not-allowed" : "pointer",
                  }}
                >
                  {isSubmitting ? (
                    <CircularProgress size={20} sx={{ color: "white" }} />
                  ) : (
                    "Submit"
                  )}
                </Button>
              </Box>
            </>
          )}

          {jsonData && (
            <Box mt={3} maxHeight="300px" overflow="auto">
              <ReactJson
                src={jsonData}
                collapsed={false}
                displayDataTypes={false}
              />
            </Box>
          )}
        </Box>
      </Modal>
    </Box>
  );
}
